import type {
  BulkDuplicateRequestsBody,
  BulkDuplicateRequestsResponse,
} from "@nlc/shared-types";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue";
import {
  allCountKeys,
  invalidateQueryKeys,
  requestListKeys,
} from "@/hooks/queryKeys";
import apiClient from "@/lib/api/client";

interface UseBulkDuplicateRequestsOptions {
  onError?: (error: Error) => void;
  onSuccess?: (data: BulkDuplicateRequestsResponse) => void;
}

export function useBulkDuplicateRequests(
  options: UseBulkDuplicateRequestsOptions = {},
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      duplicateOptions: {
        includeAttachments: boolean;
        includeCollaborators: boolean;
        includeInternalNotes: boolean;
      };
      requestIds: number[];
    }) => {
      const body: BulkDuplicateRequestsBody = {
        duplicateOptions: data.duplicateOptions,
        requestIds: data.requestIds,
      };

      return await apiClient
        .url("/requests/list/bulk-duplicate")
        .post(body)
        .json<BulkDuplicateRequestsResponse>();
    },
    onError: (error: Error) => {
      toast.add({
        description: error.message || "Failed to duplicate requests",
        title: "Error duplicating requests",
        type: "error",
      });
      options.onError?.(error);
    },
    onSuccess: (data) => {
      invalidateQueryKeys(queryClient, [requestListKeys.all, ...allCountKeys]);
      options.onSuccess?.(data);
    },
  });
}
